import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Button,
  ContentSwitcher,
  DatePicker,
  DatePickerInput,
  DismissibleTag,
  FilterableMultiSelect,
  Search,
  Switch,
} from "@carbon/react";
import { useCategoriesByType } from "../../hooks/useCategories";
import { useGetTagsInUse } from "../../hooks/useTags";
import { useUsersByCompany } from "../../hooks/useUsers";
import { useAnalyticsSupplierOptions } from "../../hooks/useAnalyticsSupplierOptions";
import { toIsoDate } from "../../components/KPIs/utils/kpiHelpers";
import {
  AnalyticsFilters,
  CategoryResponse,
  MaterialType,
  PurchaseRequestItemState,
} from "../../types";

export const SEARCH_DEBOUNCE_MS = 300;

const PRESET_MONTHS = [3, 6, 12, 24] as const;
type Preset = (typeof PRESET_MONTHS)[number];
const PRESET_KEYS: Record<Preset, string> = {
  3: "analytics.filters.preset3m",
  6: "analytics.filters.preset6m",
  12: "analytics.filters.preset12m",
  24: "analytics.filters.preset24m",
};
const DEFAULT_PRESET: Preset = 12;

/** `[startDate, endDate]` (ISO yyyy-MM-dd) covering the last `months` months up to `today`. */
export const presetRange = (months: number, today: Date = new Date()) => {
  const start = new Date(
    today.getFullYear(),
    today.getMonth() - months,
    today.getDate()
  );
  return { startDate: toIsoDate(start), endDate: toIsoDate(today) };
};

/**
 * The filters the dashboard starts from — last 12 months, nothing else. The dashboard query is
 * disabled until both dates are set, so the page MUST seed its filter state with this.
 */
export const getDefaultAnalyticsFilters = (
  today: Date = new Date()
): AnalyticsFilters => presetRange(DEFAULT_PRESET, today);

const parseIsoDate = (iso?: string): Date | undefined => {
  if (!iso) return undefined;
  const [y, m, d] = iso.split("-").map(Number);
  return y && m && d ? new Date(y, m - 1, d) : undefined;
};

const detectPreset = (value: AnalyticsFilters): Preset | "custom" => {
  const match = PRESET_MONTHS.find((months) => {
    const range = presetRange(months);
    return (
      range.startDate === value.startDate && range.endDate === value.endDate
    );
  });
  return match ?? "custom";
};

interface Option {
  id: string;
  label: string;
}

type ListKey =
  | "familyIds"
  | "tagIds"
  | "buyerIds"
  | "supplierCompanyIds"
  | "statuses";

interface FilterMultiSelectProps {
  id: string;
  label: string;
  items: Option[];
  selectedItems: Option[];
  onChange: (ids: string[]) => void;
}

const FilterMultiSelect = ({
  id,
  label,
  items,
  selectedItems,
  onChange,
}: FilterMultiSelectProps) => {
  const { t } = useTranslation();
  return (
    <FilterableMultiSelect
      id={id}
      size="md"
      titleText={label}
      placeholder={t("cta.select")}
      items={items}
      itemToString={(item) => item?.label ?? ""}
      selectedItems={selectedItems}
      onChange={({ selectedItems: next }) =>
        onChange((next ?? []).map((item) => item.id))
      }
    />
  );
};

interface AnalyticsFilterBarProps {
  /** Current filters (controlled). Seed with `getDefaultAnalyticsFilters()`. */
  value: AnalyticsFilters;
  /** Called with the full next filters on every change. */
  onChange: (filters: AnalyticsFilters) => void;
}

/**
 * Filter bar of the procurement-analytics dashboard: preset/custom date range, five Carbon
 * `FilterableMultiSelect`s (family, tag, buyer, supplier, status), a debounced material search,
 * dismissible chips for every active selection and a "Clear filters" action. Controlled — it
 * owns no filter state beyond the search box text and the active date preset.
 *
 * "Clear filters" empties the five selections and the search but keeps the date range: the
 * dashboard query is disabled without one, so a range is never "cleared", only changed.
 */
const AnalyticsFilterBar = ({ value, onChange }: AnalyticsFilterBarProps) => {
  const { t, i18n } = useTranslation();

  // ----- option sources (existing hooks; only suppliers needed a new one) -----
  const { data: indirect } = useCategoriesByType(MaterialType.INDIRECT_GOODS);
  const { data: direct } = useCategoriesByType(MaterialType.DIRECT_GOODS);
  const { data: services } = useCategoriesByType(MaterialType.SERVICES);
  const { data: tags } = useGetTagsInUse();
  // "" = keep the current user in the list (the hook's userId arg only excludes one user).
  const { data: users } = useUsersByCompany("");
  const { data: suppliers } = useAnalyticsSupplierOptions();

  const isEnglish = (i18n?.language ?? "").startsWith("en");

  const options = useMemo(() => {
    const byLabel = (a: Option, b: Option) => a.label.localeCompare(b.label);
    const families = new Map<string, Option>();
    [indirect, direct, services].forEach((list) =>
      ((list ?? []) as CategoryResponse[]).forEach((c) =>
        families.set(c.id, {
          id: c.id,
          label: (isEnglish && c.nameEn) || c.name,
        })
      )
    );
    return {
      familyIds: Array.from(families.values()).sort(byLabel),
      tagIds: (tags ?? [])
        .map((tag) => ({ id: tag.id, label: tag.name }))
        .sort(byLabel),
      buyerIds: (users ?? [])
        .map((u) => ({ id: u.id, label: u.fullName }))
        .sort(byLabel),
      supplierCompanyIds: [...(suppliers ?? [])].sort(byLabel),
      statuses: Object.values(PurchaseRequestItemState).map((state) => ({
        id: state,
        label: t(`purchaseRequests.status.${state.toLowerCase()}`),
      })),
    } as Record<ListKey, Option[]>;
  }, [indirect, direct, services, tags, users, suppliers, isEnglish, t]);

  const lists: { key: ListKey; label: string }[] = [
    { key: "familyIds", label: t("analytics.filters.family") },
    { key: "tagIds", label: t("analytics.filters.tag") },
    { key: "buyerIds", label: t("analytics.filters.buyer") },
    { key: "supplierCompanyIds", label: t("analytics.filters.supplier") },
    { key: "statuses", label: t("analytics.filters.status") },
  ];

  // Selected ids resolved back to the (identity-stable) option objects Carbon matches on; an id
  // the list doesn't contain (yet) is kept as an id-labelled placeholder so it stays removable.
  const selectedFor = (key: ListKey): Option[] => {
    const wanted = value[key] ?? [];
    return wanted.map(
      (id) => options[key].find((o) => o.id === id) ?? { id, label: id }
    );
  };

  const setList = (key: ListKey, ids: string[]) =>
    onChange({ ...value, [key]: ids.length ? ids : undefined });

  // ----- date range -----
  const [preset, setPreset] = useState<Preset | "custom">(() =>
    detectPreset(value)
  );

  const selectPreset = (months: Preset) => {
    setPreset(months);
    onChange({ ...value, ...presetRange(months) });
  };

  const datePickerValue = [
    parseIsoDate(value.startDate),
    parseIsoDate(value.endDate),
  ].filter((d): d is Date => !!d);

  const handleCustomRange = (dates: Date[]) => {
    if (dates.length !== 2) return;
    const startDate = toIsoDate(dates[0]);
    const endDate = toIsoDate(dates[1]);
    if (startDate === value.startDate && endDate === value.endDate) return;
    setPreset("custom");
    onChange({ ...value, startDate, endDate });
  };

  // ----- material search (debounced) -----
  const [searchText, setSearchText] = useState(value.materialQuery ?? "");
  const timer = useRef<ReturnType<typeof setTimeout>>();
  // Latest props for the debounced callback, which fires after the render that scheduled it.
  const latest = useRef({ value, onChange });
  latest.current = { value, onChange };

  useEffect(() => () => clearTimeout(timer.current), []);

  const handleSearch = (text: string) => {
    setSearchText(text);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      const { value: current, onChange: emit } = latest.current;
      const query = text.trim() || undefined;
      if (query !== current.materialQuery) {
        emit({ ...current, materialQuery: query });
      }
    }, SEARCH_DEBOUNCE_MS);
  };

  // ----- chips + clear -----
  const chips = lists.flatMap(({ key, label }) =>
    selectedFor(key).map((option) => ({ key, label, option }))
  );
  const hasActive = chips.length > 0 || !!value.materialQuery || !!searchText;

  const clearAll = () => {
    clearTimeout(timer.current);
    setSearchText("");
    onChange({ startDate: value.startDate, endDate: value.endDate });
  };

  return (
    <div
      className="analytics-filter-bar"
      style={{ display: "flex", flexDirection: "column", gap: "1rem" }}
    >
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "flex-end",
          gap: "1rem",
        }}
      >
        <div>
          <span className="cds--label">{t("analytics.filters.dateRange")}</span>
          <ContentSwitcher
            size="md"
            selectedIndex={PRESET_MONTHS.indexOf(preset as Preset)}
            onChange={({ index }) => {
              if (index !== undefined) selectPreset(PRESET_MONTHS[index]);
            }}
          >
            {PRESET_MONTHS.map((months) => (
              <Switch
                key={months}
                name={String(months)}
                text={t(PRESET_KEYS[months])}
              />
            ))}
          </ContentSwitcher>
        </div>

        <DatePicker
          datePickerType="range"
          dateFormat="Y-m-d"
          value={datePickerValue}
          onChange={handleCustomRange}
        >
          <DatePickerInput
            id="analytics-filter-start"
            labelText={t("analytics.startDate")}
            placeholder="YYYY-MM-DD"
            size="md"
          />
          <DatePickerInput
            id="analytics-filter-end"
            labelText={t("analytics.endDate")}
            placeholder="YYYY-MM-DD"
            size="md"
          />
        </DatePicker>

        <div style={{ flex: "0 1 20rem", minWidth: "12rem" }}>
          <Search
            id="analytics-filter-material"
            size="md"
            labelText={t("analytics.filters.searchLabel")}
            placeholder={t("analytics.filters.searchPlaceholder")}
            value={searchText}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              handleSearch(e.target.value)
            }
            onClear={() => handleSearch("")}
          />
        </div>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(14rem, 1fr))",
          gap: "1rem",
        }}
      >
        {lists.map(({ key, label }) => (
          <FilterMultiSelect
            key={key}
            id={`analytics-filter-${key}`}
            label={label}
            items={options[key]}
            selectedItems={selectedFor(key).filter((s) =>
              options[key].includes(s)
            )}
            onChange={(ids) => setList(key, ids)}
          />
        ))}
      </div>

      {hasActive && (
        <div
          role="group"
          aria-label={t("analytics.filters.active")}
          style={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            gap: "0.5rem",
          }}
        >
          {chips.map(({ key, label, option }) => (
            <DismissibleTag
              key={`${key}:${option.id}`}
              type="gray"
              size="md"
              text={`${label}: ${option.label}`}
              title={t("cta.remove")}
              onClose={() =>
                setList(
                  key,
                  (value[key] ?? []).filter((id) => id !== option.id)
                )
              }
            />
          ))}
          <Button kind="ghost" size="sm" onClick={clearAll}>
            {t("analytics.filters.clear")}
          </Button>
        </div>
      )}
    </div>
  );
};

export default AnalyticsFilterBar;
